import _ from 'lodash'
import { APIServer } from '@/app/data/api'
import { useAuthStore } from '@/app/data/auth'
import type { ChangedItem, DefectPhotoType } from '@/app/data/type'
import { CloudFile } from '@/lib/CloudFile'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { convertFileToWebp } from '@/app/camera'
import { AppFileManager } from '@/app/file'
import { SQL } from '@/lib/Sqlite'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import {
  parseRow,
  isPreviousPhoto,
  uploadPathGenerator,
  sortByKeys,
  getLastSortKey,
  getFirstSortKey,
  isOutSidePhoto,
  handleCapturedPhotoClone,
  syncCacheWithServerUrl,
  isPreviousShape,
} from '@/app/util'
import { getRandomString } from '@/lib/Functions'
import { GCS_ROOT_PATH, isApp } from '@/index'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { Network } from '@capacitor/network'
import { findChangedItems, logChanges, useDeviceEventLogStore } from '@/app/store/deviceEventLog'
import { syncQueue, JobPriority, JobStatus } from '@/app/sync'
import { filterStaleMergedItems } from '@/app/sync/mergeWriteGuard'
import { isRowDeletedLocally, hasLocalChangesSince } from '@/app/sync/jobGuards'
import { SyncManager } from '@/app/sync/syncManager'
import { isSyncRejectedActive } from '@/app/sync/syncRejection'
import { DependencyNotReadyError, DependencyRejectedError } from '@/app/sync/syncError'
import { useUndoRedo } from '../../undoRedo'
import { useCanvasStore } from '../../store/canvas'
import { useShapeStore } from './shape'
import { useAllTaskStore } from '../../store/task'
import { getCurrentColor, getCurrentColorName, PREV_DEFECT_STYLE } from '../../constant'
import {
  saveCurrentStylesToShapeData,
  consumeSavedCurrentStyles,
} from '@/app/function/shapeColorToggle'
import { LocalFileResourcePath } from '../../camera/type'
import { DefectPhoto, Defect, CanvasNotFoundHandler } from '../types'
import type { PhotoDrawing } from '../../photoDrawing/types'
import { isSamePhotoDrawing } from '../../photoDrawing/validate'
import Merge from '../../../lib/Task/merge'
import SQLite from '../../../lib/Task/sqlite'
import { base64ToFile } from '@/lib/Functions/files'
import UTIL from '../../../lib/CanvasEngine/helper/canvasUtil'
import { App } from '../..'
import {
  FileMissingError,
  registerFileMissing,
  clearFileMissingCount,
  isFileNotFoundError,
  isFileMissingConfirmed,
} from './photoFileGuard'

//TODO 0917 android-file, android-local-file 관련 수정해야함 local-file이 맞음
// 내부 저장용으로 사용되는 데이터 형식
const storeKey = 'defect_photos'

const PHOTO_TYPE = {
  NEW: 'new',
  PREV: 'prev',
} as const

const getLocalData = async (taskId: string): Promise<DefectPhoto[]> => {
  if (!taskId) return []
  const db = await SQL.getDatabase()
  try {
    // 테이블이 없는 경우 빈 배열 반환
    if (!SQL.createdTables.has(storeKey)) {
      throw new Error('테이블이 존재하지 않습니다.')
    }

    // 테이블이 존재하는 경우에만 쿼리 실행
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
    return localData.values?.map((row: DefectPhoto) => parseRow(row))
  } catch (error) {
    console.error('로컬 결함 사진 가져오기 실패', error)
    return []
  }
}

export const useDefectPhotoStore = defineStore('defect_photos', () => {
  const photos = ref<DefectPhoto[]>([])
  const currentTaskDatabaseId = ref<string>('')

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  const notUploadedDefectPhotos = computed(() => {
    return photos.value.filter(item => item.modifiedFromLocal)
  })
  async function uploadNotUploaded(): Promise<void> {
    if (notUploadedDefectPhotos.value.length > 0) {
      await requestUpdate(notUploadedDefectPhotos.value)
    }
  }

  // watch 제거: 각 함수에서 직접 로깅 처리
  async function individualSetLocalModified(
    localStringId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    keyValues: { key: string; value: any }[] = [],
    noLogs: boolean = false,
  ): Promise<void> {
    const db = await SQL.getDatabase()

    // id 교체 시 동일 localStringId의 유령 row가 남아 있으면 먼저 정리
    // (두 row가 같은 id로 동시 갱신되며 PRIMARY KEY 충돌로 UPDATE 전체가 실패하는 것을 방지)
    if (keyValues.some(keyValue => keyValue.key === 'id')) {
      const duplicated = await db.query(`SELECT * FROM ${storeKey} WHERE localStringId = ?`, [
        localStringId,
      ])
      if (duplicated.values && duplicated.values.length > 1) {
        await removeGhostRows(
          duplicated.values.map((row: DefectPhoto) => parseRow(row)) as DefectPhoto[],
        )
      }
    }

    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      const keyValue = keyValues[i]
      sql += `${keyValue.key} = ?`
      if (i < keyValues.length - 1) sql += ', '
    }
    sql += ' WHERE localStringId = ?'
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    // 로깅 처리 (이벤트 발행 전에 이전 값 기록)
    if (!noLogs) {
      const photoIndex = photos.value.findIndex(item => item.localStringId === localStringId)
      if (photoIndex !== -1) {
        const previousPhoto = { ...photos.value[photoIndex] }
        // 임시로 새 값 적용하여 변경사항 비교
        const tempPhoto = { ...previousPhoto }
        for (const keyValue of keyValues) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(tempPhoto as any)[keyValue.key] = keyValue.value
        }
        const changes: ChangedItem[] = findChangedItems(
          [{ ...previousPhoto, localStringId: previousPhoto.localStringId }],
          [{ ...tempPhoto, localStringId: tempPhoto.localStringId }],
        )
        logChanges('DEFECT_PHOTOS', changes)
      }
    }

    // 이벤트 발행 (리스너 완료 대기)
    await SQLiteEventBus.getInstance().emitUpdate('defect_photos', 'update', [localStringId])
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId - 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    // 기존 리스너 해제 (중복 등록 방지)
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('defect_photos', currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        // 테이블이 아직 생성되지 않은 경우 스킵
        if (!SQL.createdTables.has(storeKey)) return

        const db = await SQL.getDatabase()

        if (event.type === 'bulk' || event.ids.length === 0) {
          // 대량 업데이트: 전체 로드
          const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
          photos.value = localData.values?.map((row: unknown) => parseRow(row)) as DefectPhoto[]
          // backgroundSync 후 전차 결함 색상 재평가
          await reevaluateAllPrevColors()
        } else {
          // 개별 업데이트: 해당 항목만 갱신
          for (const id of event.ids) {
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
              [id, taskId],
            )

            if (result.values && result.values.length > 0) {
              const updatedItem = parseRow(result.values[0]) as DefectPhoto
              const index = photos.value.findIndex(p => p.localStringId === id)

              if (index !== -1) {
                // 기존 항목 업데이트 (deleted_at 포함 - restore를 위해 메모리에 유지)
                photos.value[index] = updatedItem
              } else {
                // 새 항목 추가
                photos.value.push(updatedItem)
              }
            }
          }
        }
      } catch (error) {
        console.error('[DefectPhotoStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated('defect_photos', currentListener)
  }

  async function getServerData(taskId: string): Promise<DefectPhoto[]> {
    if (!taskId) return []
    try {
      const data: DefectPhoto[] = await APIServer.getDefectPhotoListByTaskId(taskId)
      return data
        .filter(item => !item.deleted_at)
        .map(item => {
          return {
            ...item,
            localStringId: item.id.toString(),
            localShapeId: item.defect_id.toString(),
            modifiedFromLocal: false,
          } as DefectPhoto
        })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  const addApiQueue = async (
    itemsToProcess: { item: DefectPhoto; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return

    const shapeStore = useShapeStore()
    for (const { item } of itemsToProcess) {
      // 로컬에서 변경된 항목만 동기화 대상으로 삼습니다.
      if (!item.modifiedFromLocal) continue
      // 부모 결함 생성이 서버 거절로 멈춘 미업로드 사진: job을 만들지 않는다 (부모 생성 시 onIdMap이 재투입).
      // 메모리 결함은 현재 층만 있으므로 못 찾으면 job을 만들고, execute가 1회 판정 후 종료시킨다.
      if (String(item.id).includes('_need_to_upload') && item.localShapeId) {
        const parent = shapeStore.GetItemRaw(String(item.localShapeId))
        if (
          parent &&
          parent.modifiedFromLocal &&
          String(parent.id).includes('_need_to_upload') &&
          isSyncRejectedActive(parent)
        ) {
          console.warn(`[DefectPhoto] 부모 결함 서버 거절로 동기화 보류: ${item.localStringId}`)
          syncQueue.markDependencyBlocked(String(item.localShapeId))
          continue
        }
      }
      addPhotoJobToQueue(item)
    }
  }

  /**
   * 부모 결함이 서버에 생성된 뒤, 그 결함에 달린 미동기화 사진을 다시 큐에 넣는다.
   * 부모 거절 동안 종료(DependencyRejectedError)·생략된 job은 큐에 없어 wakeFailedJobs로는 살아나지 않는다.
   * 이미 대기·진행 중인 job은 addPhotoJobToQueue의 중복 검사가 거른다.
   * @param shapeLocalStringId 생성된 부모 결함의 localStringId
   */
  async function requeuePhotosOfShape(shapeLocalStringId: string): Promise<void> {
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT * FROM ${storeKey} WHERE localShapeId = ? AND modifiedFromLocal = 1`,
      [shapeLocalStringId],
    )
    const rows = (result.values ?? []).map((row: unknown) => parseRow(row) as DefectPhoto)
    if (rows.length === 0) return
    console.info(
      `[DefectPhoto] 부모 결함 생성 → 사진 ${rows.length}건 재투입: ${shapeLocalStringId}`,
    )
    for (const row of rows) addPhotoJobToQueue(row)
  }

  const requestUpdate = async (datas: DefectPhoto[]): Promise<void> => {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
      datas as unknown as Defect[],
      storeKey,
    )
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: DefectPhoto; itemId: string }[])
  }
  async function onlySetSqlite(items: DefectPhoto[]): Promise<void> {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
      items as unknown as DefectPhoto[],
      storeKey,
    )
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
  }
  /**
   * 파일 부재 사진 처리: 유예 중에는 FileMissingError로 재시도를 유도하고,
   * 부재 확정 시 동기화 대상에서 제외한다 (행과 '기기에서 삭제됨' 배지는 유지)
   * @param data 대상 사진
   * @param localFilePath 누락된 로컬 파일 경로
   * @returns 확정 시 동기화 종료 처리된 사진 (job 성공으로 처리)
   */
  async function handleMissingPhotoFile(
    data: DefectPhoto,
    localFilePath: string,
  ): Promise<DefectPhoto> {
    if (registerFileMissing(data.localStringId, '결함') === 'retry') {
      throw new FileMissingError(`File not found: ${localFilePath}`)
    }
    await individualSetLocalModified(data.localStringId, [
      { key: 'modifiedFromLocal', value: false },
    ])
    return { ...data, modifiedFromLocal: false } as DefectPhoto
  }

  /**
   * job 실행 스냅샷 이후 로컬 삭제·수정이 끼어들지 않아 row를 동기화 완료로 내릴 수 있는지 판정.
   * 끼어든 경우 modifiedFromLocal을 유지해야 삭제 job 또는 후속 update job이 서버로 전파된다.
   * @param data job 실행 스냅샷
   */
  async function canMarkSynced(data: DefectPhoto): Promise<boolean> {
    if (await isRowDeletedLocally(storeKey, data.localStringId)) return false
    return !(await hasLocalChangesSince(storeKey, data.localStringId, data.fields_updated_at))
  }

  /**
   * job 실행 중 끼어든 로컬 수정(대표사진 변경 등)을 최신 row로 이어서 전파한다.
   * 현재 job이 PROCESSING 상태이므로 SyncQueue 대기열에 들어가 완료 직후 실행된다.
   * @param localStringId 대상 사진의 localStringId
   */
  async function enqueueFollowUpJob(localStringId: string): Promise<void> {
    const db = await SQL.getDatabase()
    const result = await db.query(`SELECT * FROM ${storeKey} WHERE localStringId = ?`, [
      localStringId,
    ])
    if (!result.values || result.values.length === 0) return
    const latest = parseRow(result.values[0]) as DefectPhoto
    if (latest.deleted_at) return
    // 완료 처리(mfl=false)와 경합해 dirty가 내려간 경우 되살린다
    if (!latest.modifiedFromLocal) {
      await individualSetLocalModified(
        localStringId,
        [{ key: 'modifiedFromLocal', value: true }],
        true,
      )
      latest.modifiedFromLocal = true
    }
    console.info(`[PhotoGuard] ${localStringId}: job 실행 중 로컬 수정 감지 - 후속 update job 등록`)
    await addPhotoJobToQueue(latest, true)
  }

  /**
   * 서버 응답의 필드 스탬프를 로컬 스탬프와 필드별 최신값으로 병합한다.
   * 이후 로컬 변경 스탬프가 항상 서버 스탬프 뒤에 오게 되어, 기기 시계가 서버보다 늦을 때
   * 다음 변경이 병합에서 서버의 옛 값에 밀리는 것을 방지한다.
   * @param local 로컬 fields_updated_at
   * @param server 서버 응답 fields_updated_at
   * @returns 병합 결과 (서버 쪽이 더 늦은 스탬프가 없으면 null)
   */
  function mergeServerFieldStamps(local: unknown, server: unknown): Record<string, string> | null {
    if (!server || typeof server !== 'object') return null
    const merged: Record<string, string> =
      local && typeof local === 'object' ? { ...(local as Record<string, string>) } : {}
    let changed = false
    for (const [field, stamp] of Object.entries(server as Record<string, string>)) {
      const serverTime = new Date(stamp).getTime()
      if (isNaN(serverTime)) continue
      const localTime = merged[field] ? new Date(merged[field]).getTime() : Number.NaN
      if (isNaN(localTime) || serverTime > localTime) {
        merged[field] = stamp
        changed = true
      }
    }
    return changed ? merged : null
  }

  /**
   * DefectPhoto 동기화 작업을 SyncQueue에 추가하는 헬퍼 함수
   * @param item 동기화할 DefectPhoto 데이터
   * @param allowWhileInFlight 진행 중 job이 있어도 등록 (후속 job 전용 - SyncQueue 대기열로 들어감)
   */
  async function addPhotoJobToQueue(
    item: DefectPhoto,
    allowWhileInFlight: boolean = false,
  ): Promise<void> {
    const isNew = String(item.id).includes('_need_to_upload')
    const isRestore = !isNew && !item.deleted_at && item.fields_updated_at?.need_restore
    const isDeleted = item.deleted_at && !item.fields_updated_at?.need_restore
    const needsFileUpload =
      item.url.startsWith('local-file://') ||
      item.url.startsWith('data:image') ||
      item.url.startsWith('android-local-file:/') ||
      item.url.startsWith('android-file:/')

    // 기존 코드의 진행 순서를 유지하기 위한 executeCallback 정의
    let executeCallback: (data: DefectPhoto) => Promise<DefectPhoto | DefectPhoto | void>
    let jobPriority = JobPriority.NORMAL

    // 중복 작업 방지를 위한 검사
    // 이미 동일한 localStringId로 큐에 작업이 있는지 확인
    const existingJobs = syncQueue
      .getQueueState()
      .filter(
        job =>
          job.localStringId === item.localStringId &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING),
      )

    // 삭제 요청은 진행 중인 업로드 job이 있어도 큐에 넣어야 한다
    // (업로드 완료 후 삭제가 이어서 서버로 전파되도록 — 스킵하면 서버에 사진이 남음)
    // 대기(PENDING) job은 실행 시 SQLite 최신 row를 다시 읽으므로 스킵해도 변경이 반영되지만,
    // 진행(PROCESSING) 중 끼어든 변경은 완료 시점의 가드(canMarkSynced)가 후속 job으로 이어준다
    if (existingJobs.length > 0 && !isDeleted && !allowWhileInFlight) {
      console.warn(
        `[DefectPhoto] Job for ${item.localStringId} already exists in queue. Skipping duplicate.`,
      )
      return
    }

    if (isDeleted) {
      // 사진이 삭제되면 파일 부재 이력도 함께 정리
      clearFileMissingCount(item.localStringId)
      // 서버에 생성된 적 없고 진행 중인 업로드 job도 없는 사진은 서버에 알릴 필요가 없음
      if (isNew && existingJobs.length === 0) {
        await individualSetLocalModified(item.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return
      }
      executeCallback = async (data: DefectPhoto): Promise<DefectPhoto> => {
        // 실행 시점 기준 서버 미생성 사진(업로드가 삭제로 무효화된 경우): 서버 호출 없이 종료
        if (String(data.id).includes('_need_to_upload')) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        await APIServer.deleteDefectPhoto([{ id: Number(data.id) }])
        await onlySetSqlite([data])
        // onSuccess의 mfl 정리는 enqueue 시점 isNew에 의존하므로 여기서 직접 완료 처리
        await individualSetLocalModified(data.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return data
      }
    } else if (isNew) {
      jobPriority = JobPriority.LOW
      executeCallback = async (data: DefectPhoto): Promise<DefectPhoto> => {
        // 업로드 전에 로컬에서 삭제된 사진: 서버에 생성하지 않고 동기화 종료
        if (data.deleted_at) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        const localShapeId = _.get(data, 'localShapeId') ?? data.defect_id.toString()
        const shape_id = await syncQueue.findShapeId(localShapeId)
        //shape가 삭제된 경우
        if (shape_id === 'is_deleted') {
          const photoDeleteTime = Merge.getCurrentTime()
          await individualSetLocalModified(data.localStringId, [
            { key: 'deleted_at', value: photoDeleteTime },
            { key: 'modifiedFromLocal', value: false },
          ])
          return {
            ...data,
            deleted_at: photoDeleteTime,
            modifiedFromLocal: false,
          } as DefectPhoto
        }
        if (
          String(shape_id).includes('_need_to_upload') ||
          isNaN(Number(shape_id)) ||
          shape_id === -1
        ) {
          // 부모 결함 생성이 서버 거절로 멈췄으면 기다려도 풀리지 않는다 — job 종료(부모 생성 시 onIdMap이 재투입)
          if (await syncQueue.isDependencyRejected(String(localShapeId))) {
            throw new DependencyRejectedError(
              `Dependent shape ${localShapeId} was rejected by server.`,
            )
          }
          throw new DependencyNotReadyError(
            `Dependent shape ${data.localShapeId} is not synced yet.`,
          )
        }

        // 신규 로직: 파일 업로드를 먼저 수행
        if (needsFileUpload) {
          // storage://로 시작하면 이미 GCS 업로드 완료 (SQLite만 업데이트된 상태)
          // 이 경우 MySQL 레코드만 생성하면 됨
          if (data.url.startsWith('storage://')) {
            const photoData = {
              defect_id: Number(shape_id),
              url: data.url, // 이미 업로드된 GCS URL 사용
              is_previous: data.is_previous,
              inspection_type: data.inspection_type,
              sort_key: data.sort_key,
              // 첫 업로드 전에 그린 그림은 생성 페이로드로만 올라간다
              drawing: data.drawing ?? null,
              // 오프라인 촬영 후 동기화 시 서버 도착 시각이 아닌 실제 촬영 시각 보존
              created_at: data.created_at,
              updated_at: data.updated_at,
              fields_updated_at: data.fields_updated_at,
            }

            const [photoResult] = await APIServer.addDefectPhoto([
              Merge.returnHasValue(photoData) as DefectPhoto,
            ])
            if (!photoResult || !photoResult.id) {
              throw new Error('Failed to create photo record on server.')
            }
            // await onlySetSqlite([{ ...photoResult } as DefectPhoto])

            // onIdMap 실패 시에도 modifiedFromLocal=false가 보장됨
            // (단, 업로드 도중 로컬 삭제·수정(대표 변경 등)이 끼어든 경우 mfl을 유지해 후속 job이 전파)
            const updateKeys: { key: string; value: unknown }[] = [
              { key: 'id', value: photoResult.id },
              { key: 'defect_id', value: photoResult.defect_id },
            ]
            if (await canMarkSynced(data)) {
              updateKeys.push({ key: 'modifiedFromLocal', value: false })
            }
            if (photoResult.facility_id) {
              updateKeys.push({ key: 'facility_id', value: photoResult.facility_id })
            }
            if (photoResult.floorplan_id) {
              updateKeys.push({ key: 'floorplan_id', value: photoResult.floorplan_id })
            }
            await individualSetLocalModified(data.localStringId, updateKeys)

            return { ...photoResult, url: data.url }
          }

          // 파일명 생성: {shape_id}_{localStringId}_{timestamp}.webp
          const tempFileName = `${shape_id}_${data.localStringId}_${Date.now()}.webp`
          let file: File | undefined
          let webpBase64: string | undefined
          let localFilePath: LocalFileResourcePath | undefined

          if (
            data.url.startsWith('local-file://') ||
            data.url.startsWith('android-local-file:/') ||
            data.url.startsWith('android-file:/')
          ) {
            localFilePath = data.url as LocalFileResourcePath

            // 파일 존재 확인 (확인된 404만 missing 판정)
            if (!(await AppFileManager.checkFileExists(localFilePath))) {
              return handleMissingPhotoFile(data, localFilePath)
            }

            try {
              const converted = await convertFileToWebp({ resourcePath: localFilePath })
              if (!converted.value) {
                throw new Error('File conversion to WebP failed.')
              }
              webpBase64 = converted.value
            } catch (error) {
              // 사전 확인이 통과해도 네이티브 변환의 파일 없음(ENOENT)은 부재 처리로 위임
              if (!isFileNotFoundError(error)) throw error
              return handleMissingPhotoFile(data, localFilePath)
            }
            clearFileMissingCount(data.localStringId)
            file = await base64ToFile(webpBase64, tempFileName)
          } else if (data.url.startsWith('data:image')) {
            file = await base64ToFile(data.url, tempFileName)
          }

          if (!file) throw new Error('File conversion failed for new photo.')

          const splitPath = localFilePath ? localFilePath.split('_') : []
          const taskDatabaseId = splitPath[4] ?? currentTaskDatabaseId.value ?? 'local'
          const facilityBranchDatabaseId = splitPath[5] ?? 'local'
          const floorBranchDatabaseId = splitPath[6] ?? 'local'

          const uploadPath = uploadPathGenerator(
            taskDatabaseId,
            facilityBranchDatabaseId,
            floorBranchDatabaseId,
          )
          const typeUrl = data.is_previous
            ? `${uploadPath}/prev/${file.name}`
            : `${uploadPath}/${file.name}`

          // 1단계: 파일을 버킷에 먼저 업로드
          const resourceUrl = await CloudFile.uploadFile(
            'homecheck-service',
            `${GCS_ROOT_PATH}/${typeUrl}`,
            file,
          )

          if (!resourceUrl) throw new Error('Cloud upload failed for new photo.')

          // storage://로 시작하는지 확인 (전제조건 1)
          if (!resourceUrl.startsWith('storage://')) {
            throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
          }

          // 2단계: GCS 업로드 성공 시 SQLite와 job 데이터에 즉시 url 반영
          // 이후 재시도가 storage:// 분기로 진입해 파일 재업로드와 서버 레코드 중복 생성을 막는다
          await individualSetLocalModified(data.localStringId, [{ key: 'url', value: resourceUrl }])
          data.url = resourceUrl

          // 촬영 사진을 앱 디렉토리로 복사
          await handleCapturedPhotoClone({ ...data, resourcePath: localFilePath })

          // 서버 파일명으로 캐시에 복사 (오프라인)
          await syncCacheWithServerUrl(localFilePath || '', resourceUrl)

          // 3단계: 파일 업로드 성공 후, 버킷 경로로 레코드 생성
          const photoData = {
            defect_id: Number(shape_id),
            url: resourceUrl, // 버킷 경로로 생성
            is_previous: data.is_previous,
            inspection_type: data.inspection_type,
            sort_key: data.sort_key,
            // 첫 업로드 전에 그린 그림은 생성 페이로드로만 올라간다
            drawing: data.drawing ?? null,
            // 오프라인 촬영 후 동기화 시 서버 도착 시각이 아닌 실제 촬영 시각 보존
            created_at: data.created_at,
            updated_at: data.updated_at,
            fields_updated_at: data.fields_updated_at,
          }

          const [photoResult] = await APIServer.addDefectPhoto([photoData])
          if (!photoResult || !photoResult.id) {
            throw new Error('Failed to create photo record on server.')
          }

          // onIdMap 실패 시에도 modifiedFromLocal=false가 보장됨
          // (단, 업로드 도중 로컬 삭제·수정(대표 변경 등)이 끼어든 경우 mfl을 유지해 후속 job이 전파)
          const updateKeys2: { key: string; value: unknown }[] = [
            { key: 'id', value: photoResult.id },
            { key: 'defect_id', value: photoResult.defect_id },
          ]
          if (await canMarkSynced(data)) {
            updateKeys2.push({ key: 'modifiedFromLocal', value: false })
          }
          if (photoResult.facility_id) {
            updateKeys2.push({ key: 'facility_id', value: photoResult.facility_id })
          }
          if (photoResult.floorplan_id) {
            updateKeys2.push({ key: 'floorplan_id', value: photoResult.floorplan_id })
          }
          await individualSetLocalModified(data.localStringId, updateKeys2)

          // 최종 성공 결과를 반환
          return { ...photoResult, url: resourceUrl }
        }

        // 파일 업로드가 필요 없는 경우 (드문 케이스)
        const photoData = {
          defect_id: Number(shape_id),
          url: data.url,
          is_previous: data.is_previous,
          inspection_type: data.inspection_type,
          sort_key: data.sort_key,
          // 첫 업로드 전에 그린 그림은 생성 페이로드로만 올라간다
          drawing: data.drawing ?? null,
          // 오프라인 촬영 후 동기화 시 서버 도착 시각이 아닌 실제 촬영 시각 보존
          created_at: data.created_at,
          updated_at: data.updated_at,
          fields_updated_at: data.fields_updated_at,
        }
        const [photoResult] = await APIServer.addDefectPhoto([photoData])

        const updateKeys3: { key: string; value: unknown }[] = [
          { key: 'id', value: photoResult.id },
          { key: 'defect_id', value: photoResult.defect_id },
        ]
        if (await canMarkSynced(data)) {
          updateKeys3.push({ key: 'modifiedFromLocal', value: false })
        }
        if (photoResult.facility_id) {
          updateKeys3.push({ key: 'facility_id', value: photoResult.facility_id })
        }
        if (photoResult.floorplan_id) {
          updateKeys3.push({ key: 'floorplan_id', value: photoResult.floorplan_id })
        }
        await individualSetLocalModified(data.localStringId, updateKeys3)

        return photoResult
      }
    } else if (isRestore) {
      // undo로 복원된 항목은 따로처리
      // restore api를 탈 수 있는 요소는 모두 동기화 완료된 요소임.
      executeCallback = async (data: DefectPhoto): Promise<void> => {
        const defectPhotoId = Number(data.id)
        await APIServer.restoreDefectPhoto([{ id: defectPhotoId }])
        await APIServer.updateDefectPhoto([
          {
            ...data,
            id: defectPhotoId,
          },
        ])
        await individualSetLocalModified(data.localStringId, [{ key: 'deleted_at', value: null }])
      }
    } else {
      // 업데이트 로직
      if (needsFileUpload) {
        jobPriority = JobPriority.LOW
        executeCallback = async (data: DefectPhoto): Promise<DefectPhoto | DefectPhoto> => {
          // storage://로 시작하면 이미 업로드 완료된 것으로 간주 (전제조건 1)
          if (data.url.startsWith('storage://')) {
            return data
          }

          const fileName = `${data.id}_${Date.now()}.webp`
          let file: File | undefined
          let webpBase64: string | undefined
          let localFilePath: LocalFileResourcePath | undefined

          const isPrevPhoto = data.url.includes('_prev')
          const currentType = isPrevPhoto ? PHOTO_TYPE.PREV : PHOTO_TYPE.NEW

          if (
            data.url.startsWith('local-file://') ||
            data.url.startsWith('android-local-file:/') ||
            data.url.startsWith('android-file:/')
          ) {
            localFilePath = data.url as LocalFileResourcePath

            // 파일 존재 확인 (확인된 404만 missing 판정)
            if (!(await AppFileManager.checkFileExists(localFilePath))) {
              return handleMissingPhotoFile(data, localFilePath)
            }

            try {
              const converted = await convertFileToWebp({ resourcePath: localFilePath })
              if (!converted.value) {
                throw new Error('File conversion to WebP failed.')
              }
              webpBase64 = converted.value
            } catch (error) {
              // 사전 확인이 통과해도 네이티브 변환의 파일 없음(ENOENT)은 부재 처리로 위임
              if (!isFileNotFoundError(error)) throw error
              return handleMissingPhotoFile(data, localFilePath)
            }
            clearFileMissingCount(data.localStringId)
            file = await base64ToFile(webpBase64, fileName)
          } else if (data.url.startsWith('data:image')) {
            file = await base64ToFile(data.url, fileName)
          }

          if (!file || !localFilePath) {
            throw new Error('File conversion failed for update.')
          }

          const splitPath = localFilePath.split('_')
          const taskDatabaseId = splitPath[4] ?? currentTaskDatabaseId.value ?? 'local'
          const facilityBranchDatabaseId = splitPath[5] ?? 'local'
          const floorBranchDatabaseId = splitPath[6] ?? 'local'

          const uploadPath = uploadPathGenerator(
            taskDatabaseId,
            facilityBranchDatabaseId,
            floorBranchDatabaseId,
          )
          const typeUrl =
            currentType === PHOTO_TYPE.NEW
              ? `${uploadPath}/${file.name}`
              : `${uploadPath}/prev/${file.name}`

          // 파일을 먼저 업로드
          const resourceUrl = await CloudFile.uploadFile(
            'homecheck-service',
            `${GCS_ROOT_PATH}/${typeUrl}`,
            file,
          )

          if (!resourceUrl) throw new Error('Cloud upload failed for photo update.')

          // storage://로 시작하는지 확인 (전제조건 1)
          if (!resourceUrl.startsWith('storage://')) {
            throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
          }

          // 촬영 사진을 앱 디렉토리로 복사
          await handleCapturedPhotoClone({ ...data, resourcePath: localFilePath })

          // 서버 파일명으로 캐시에 복사 (오프라인)
          await syncCacheWithServerUrl(localFilePath || '', resourceUrl)

          // 파일 업로드 성공 후, URL 업데이트 API 호출
          const [photoResult] = await APIServer.updateDefectPhoto([
            {
              id: data.id,
              url: resourceUrl,
            },
          ])

          return { ...photoResult, url: resourceUrl }
        }
      } else {
        // 파일 업로드가 필요 없는 경우 (메타데이터만 업데이트)
        executeCallback = async (data: DefectPhoto): Promise<DefectPhoto> => {
          const [photoResult] = await APIServer.updateDefectPhoto([
            {
              id: Number(data.id),
              defect_id: data.defect_id,
              url: data.url,
              sort_key: data.sort_key,
              is_main: data.is_main,
              drawing: data.drawing ?? null,
              // 필드별 스탬프 → 서버가 더 최신 스탬프의 필드는 덮지 않는다 (_client_ 가드)
              fields_updated_at: data.fields_updated_at,
            },
          ])
          return photoResult
        }
      }
    }

    syncQueue.addJob({
      localStringId: item.localStringId,
      data: item,
      priority: jobPriority,
      maxRetries: 5,
      tag: 'defectPhoto',
      execute: executeCallback,
      onSuccess: async (result, data) => {
        // 실행 중 끼어든 로컬 삭제·수정(대표사진 변경 등) 판정 - 끼어들었으면 dirty 유지
        const deletedLocally = await isRowDeletedLocally(storeKey, data.localStringId)
        const changedLocally =
          !deletedLocally &&
          (await hasLocalChangesSince(storeKey, data.localStringId, data.fields_updated_at))
        if (!isNew) {
          const updateValues: { key: string; value: unknown }[] = []
          if (!deletedLocally && !changedLocally) {
            updateValues.push({ key: 'modifiedFromLocal', value: false })
            const mergedStamps = mergeServerFieldStamps(
              data.fields_updated_at,
              (result as DefectPhoto | undefined)?.fields_updated_at,
            )
            if (mergedStamps) updateValues.push({ key: 'fields_updated_at', value: mergedStamps })
          }
          if (needsFileUpload && result?.url) {
            updateValues.push({ key: 'url', value: result.url })
          }
          if (updateValues.length > 0) {
            await individualSetLocalModified(data.localStringId, updateValues)
          }
        }
        if (changedLocally) await enqueueFollowUpJob(data.localStringId)
      },
      onError: async (error, data, errorCode) => {
        // 파일 부재(FileMissingError)는 가드가 이미 로깅하므로 그 외 실패만 기록
        if (!(error instanceof FileMissingError)) {
          console.error(
            `[PhotoGuard] defectPhoto job failed id=${data.localStringId}: ${error instanceof Error ? error.message : String(error)}`,
          )
        }
        //서버에 데이터가 없는(삭제된)항목에 대해 처리
        if (errorCode && errorCode === 404) {
          // restore/delete 실패는 사진만 처리, shape 삭제하지 않음
          if (isRestore || isDeleted) {
            await individualSetLocalModified(data.localStringId, [
              { key: 'modifiedFromLocal', value: false },
            ])
            syncQueue.removeJobByLocalStringId(data.localStringId)
            return
          }

          // isNew인 경우: 서버에 결함 자체가 없으므로 shape 삭제
          const canvasStore = useCanvasStore()
          const canvasHandlerState: CanvasNotFoundHandler = canvasStore.canvasNotFoundHandler(
            data.localShapeId.toString(),
            'app',
          )
          //sqlite와 메모리에서 deleted를 변경하고 syncQueue에서 제거
          await individualSetLocalModified(
            data.localStringId,
            [
              { key: 'deleted_at', value: Merge.getCurrentTime() },
              { key: 'modifiedFromLocal', value: false },
            ],
            true,
          )
          syncQueue.removeJobByLocalStringId(data.localStringId)

          // deviceEventLog에서 삭제 처리
          const eventLogStore = useDeviceEventLogStore()
          await eventLogStore.deleteLogs(data.localStringId, Merge.getCurrentTime())

          // 캔버스가 세팅되었다면 캔버스에서 해당 항목을 삭제
          if (canvasHandlerState.canvasState === 'SETTED') {
            UTIL.deleteMultipleEntities([data.localShapeId.toString()])
            UTIL.refreshCache()
            if (canvasHandlerState.shapeNum) {
              App.toastInstance.add(
                'error',
                `결함${canvasHandlerState.shapeNum}번이 서버에서 삭제되었습니다. 기기에서 삭제처리 됩니다.`,
                6000,
              )
            }
          }
          return
        }
        console.error(`[SyncQueue] Photo job for ${data.localStringId} FAILED.`, error)
      },
      onIdMap: isNew
        ? async (localStringId: string, serverResult: DefectPhoto): Promise<void> => {
            try {
              // serverResult 검증
              if (!serverResult || !serverResult.id) {
                throw new Error('Invalid serverResult: missing id or data')
              }

              // 메모리 상의 데이터 업데이트
              const photoInStore = photos.value.find(p => p.localStringId === localStringId)
              if (photoInStore) {
                photoInStore.id = serverResult.id
                photoInStore.defect_id = serverResult.defect_id
                photoInStore.url = serverResult.url
                // 업로드 도중 끼어든 로컬 수정으로 SQLite의 dirty가 유지된 경우를 존중 (SQLite 기준으로 반영)
                const db = await SQL.getDatabase()
                const rowResult = await db.query(
                  `SELECT modifiedFromLocal FROM ${storeKey} WHERE localStringId = ?`,
                  [localStringId],
                )
                photoInStore.modifiedFromLocal = Boolean(
                  Number(rowResult.values?.[0]?.modifiedFromLocal ?? 0),
                )
              }
            } catch (error) {
              console.error('[onIdMap] serverResult:', JSON.stringify(serverResult))
              // 메모리만 불일치 상태가 되지만, 다음 앱 재실행 시 SQLite에서 로드되면서 해결됨
              throw error
            }
          }
        : undefined,
    })
  }

  /**
   * 동일 localStringId의 유령 row(_need_to_upload 잔재)를 SQLite와 메모리에서 정리한다.
   * 병합 영속화 race가 만든 중복 row는 재업로드로 인한 서버 중복 레코드와
   * id 교체 시 PRIMARY KEY 충돌(UPDATE 전체 실패 → 재시도 루프)을 유발한다.
   * @param rows 검사할 row 목록
   * @returns 유령 row가 제거된 목록
   */
  async function removeGhostRows(rows: DefectPhoto[]): Promise<DefectPhoto[]> {
    const byLocalStringId = new Map<string, DefectPhoto[]>()
    for (const row of rows) {
      const key = String(row.localStringId)
      const list = byLocalStringId.get(key)
      if (list) list.push(row)
      else byLocalStringId.set(key, [row])
    }

    const ghostIds = new Set<string>()
    const db = await SQL.getDatabase()
    for (const [key, list] of byLocalStringId) {
      if (list.length < 2) continue
      const ghosts = list.filter(row => String(row.id).includes('_need_to_upload'))
      const realRows = list.filter(row => !String(row.id).includes('_need_to_upload'))
      if (ghosts.length === 0 || realRows.length === 0) continue

      console.warn(
        `[PhotoGuard] ghost row 정리: localStringId=${key} 유령 ${ghosts.length}개 삭제 (유지 id=${realRows.map(r => r.id).join(',')})`,
      )
      for (const ghost of ghosts) {
        await db.run(`DELETE FROM ${storeKey} WHERE id = ?`, [String(ghost.id)])
        ghostIds.add(String(ghost.id))
      }
      // 유지된 row가 모두 동기화 완료 상태면 유령 데이터로 대기 중인 job도 함께 제거
      if (realRows.every(row => !row.modifiedFromLocal)) {
        syncQueue.removeJobByLocalStringId(key)
      }
    }

    if (ghostIds.size === 0) return rows
    photos.value = photos.value.filter(photo => !ghostIds.has(String(photo.id)))
    return rows.filter(row => !ghostIds.has(String(row.id)))
  }

  async function initData(
    taskId: string,
    signal?: AbortSignal,
    skipServerSync = false,
  ): Promise<void> {
    currentTaskDatabaseId.value = taskId
    setupSQLiteListener(taskId)

    if (skipServerSync) return

    try {
      let mergedData = await getLocalData(taskId)
      // 동일 localStringId의 유령 row가 남아 있으면 병합 전에 정리 (재업로드 차단)
      mergedData = await removeGhostRows(mergedData)
      if (signal?.aborted) return
      // 병합 전 DB 행 값 — 병합 결과가 같은 행은 다시 쓰지 않는다
      const localSnapshot = await SQLite.snapshotRowsById(
        mergedData as unknown as Record<string, unknown>[],
      )

      let didMerge = false
      if ((await Network.getStatus()).connected) {
        const serverData = await getServerData(taskId)
        if (signal?.aborted) return

        await SQLite.checkTableExists(storeKey, serverData)

        const activeLocalData = mergedData.filter(item => !item.deleted_at)
        if (serverData.length > 0 || activeLocalData.length === 0) {
          mergedData = Merge.mergeBothData(mergedData, serverData, storeKey)
          didMerge = true
        }
      }

      if (signal?.aborted) return

      if (mergedData.length > 0) {
        const convertedMergedData = mergedData.map(item => {
          const itemId = String(item.id)
          const itemLocalStringId = String(item.localStringId)

          const needsFileUpload =
            (item.url.startsWith('android') ||
              item.url.startsWith('local-file://') ||
              item.url.startsWith('data:image')) &&
            itemId != itemLocalStringId &&
            `${itemId}` == `${itemLocalStringId}_need_to_upload` &&
            !item.deleted_at &&
            // 부재 확정 사진은 업로드 대상으로 되살리지 않음
            !isFileMissingConfirmed(String(item.localStringId))
          return {
            ...item,
            modifiedFromLocal: item.modifiedFromLocal || needsFileUpload,
          }
        })
        photos.value = convertedMergedData

        await reevaluateAllPrevColors()

        if (didMerge) {
          const isAlreadySyncing = SyncManager.isTaskSyncing(taskId)
          if (!isAlreadySyncing) {
            const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
              convertedMergedData as unknown as DefectPhoto[],
              storeKey,
            )
            const { items: changedItems, skipped } = await SQLite.skipUnchangedRows(
              itemsToProcess,
              storeKey,
              currentTaskDatabaseId.value,
              localSnapshot,
            )
            console.info(
              `[DefectPhoto] initData 병합 저장: ${changedItems.length}건 기록, ${skipped}건 변경 없음`,
            )
            // 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 저장 제외 (유령 row 부활 방지)
            const safeItems = await filterStaleMergedItems(
              changedItems as { item: DefectPhoto; itemId: string }[],
              storeKey,
              taskId,
            )
            await SQLite.updateSqliteOnly(
              safeItems,
              columns,
              currentTaskDatabaseId.value,
              storeKey,
              true,
              true,
            )
          }
        }
      }
    } catch (e) {
      if (signal?.aborted) return
      console.error('DefectPhotoStore.initData ERROR:', e)
      throw e
    }
  }

  function getDefectPhotosByTaskId(): DefectPhoto[] {
    return photos.value.filter(defectPhoto => !defectPhoto.deleted_at)
  }

  /**
   * 사진-도형 매칭에 사용할 도형 식별자 집합 (도형 localStringId + 서버 id).
   * 업로드 직후 병합 race로 사진 row가 서버형(localShapeId=서버 defect id)으로
   * 대체된 경우에도 매칭이 유지되도록 도형의 서버 id를 함께 포함한다.
   * 결함 목록/집계처럼 결함마다 반복 호출되므로 메모이즈된 맵으로 O(1) 조회한다.
   * @param shapeDatabaseId 도형의 localStringId
   */
  const resolveShapeMatchIds = (shapeDatabaseId: string): Set<string> => {
    const ids = new Set<string>([`${shapeDatabaseId}`])
    const serverShapeId = useShapeStore().shapeServerIdMap.get(`${shapeDatabaseId}`)
    if (serverShapeId) ids.add(serverShapeId)
    return ids
  }

  // 사진이 주어진 도형 식별자 집합에 속하는지 판정
  const isPhotoOfShape = (item: DefectPhoto, shapeIds: Set<string>): boolean =>
    shapeIds.has(`${item.defect_id}`) || shapeIds.has(`${_.get(item, 'localShapeId')}`)

  // 도형 식별자(defect_id·localShapeId) → 활성 사진 목록 매핑
  // 결함 목록/집계가 결함마다 photos 전체를 스캔(O(결함×사진))하지 않도록 메모이즈
  const photosByShapeKey = computed((): Map<string, DefectPhoto[]> => {
    const map = new Map<string, DefectPhoto[]>()
    const add = (key: string, photo: DefectPhoto): void => {
      const list = map.get(key)
      if (list) list.push(photo)
      else map.set(key, [photo])
    }
    for (const photo of photos.value) {
      if (photo.deleted_at) continue
      const defectKey = `${photo.defect_id}`
      const localKey = `${_.get(photo, 'localShapeId')}`
      add(defectKey, photo)
      if (localKey !== defectKey) add(localKey, photo)
    }
    return map
  })

  const getCurrentShapeDefectPhotos = (shapeDatabaseId: string): DefectPhoto[] => {
    const shapeIds = resolveShapeMatchIds(shapeDatabaseId)
    const keyedPhotos = photosByShapeKey.value
    if (shapeIds.size === 1) {
      // 호출부의 in-place sort(sortByKeys)로부터 맵 내부 배열 보호를 위해 복사본 반환
      return [...(keyedPhotos.get(`${shapeDatabaseId}`) ?? [])]
    }
    // uuid·서버 id 두 키의 목록을 합치되 같은 사진(양쪽 키에 모두 등록됨)은 한 번만
    const result: DefectPhoto[] = []
    const seen = new Set<string>()
    for (const key of shapeIds) {
      for (const photo of keyedPhotos.get(key) ?? []) {
        const photoKey = String(photo.localStringId)
        if (seen.has(photoKey)) continue
        seen.add(photoKey)
        result.push(photo)
      }
    }
    return result
  }

  const allTypePhotos = (
    shapeDatabaseId: string,
  ): {
    prevTarget: DefectPhoto | null
    newTarget: DefectPhoto | null
    prevTargetPhotos: DefectPhoto[]
    newTargetPhotos: DefectPhoto[]
    testTargetPhotos: DefectPhoto[]
  } => {
    const targetPhotos = getCurrentShapeDefectPhotos(shapeDatabaseId)
    const filteringInfoPhotos = targetPhotos.filter(p => isOutSidePhoto(p))
    const filteringTestPhotos = targetPhotos.filter(p => !isOutSidePhoto(p))
    const prevTargetPhotos = sortByKeys(filteringInfoPhotos.filter(p => isPreviousPhoto(p)))
    const newTargetPhotos = sortByKeys(filteringInfoPhotos.filter(p => !isPreviousPhoto(p)))

    return {
      prevTarget: prevTargetPhotos.length > 0 ? prevTargetPhotos[0] : null,
      // prevTarget과 동일하게 정렬 기준 첫 사진 — 다중 키 병합 순서에 의존하지 않는 결정적 선택
      newTarget: newTargetPhotos.length > 0 ? newTargetPhotos[0] : null,
      prevTargetPhotos,
      newTargetPhotos,
      testTargetPhotos: sortByKeys(filteringTestPhotos),
    }
  }

  const getCurrentPrevPhotos = (shapeId: string, type: DefectPhotoType): DefectPhoto[] => {
    const targetPhotos = getCurrentShapeDefectPhotos(shapeId)
    if (type === null) return targetPhotos
    return sortByKeys(
      targetPhotos.filter(photo =>
        type === 'new' ? !isPreviousPhoto(photo) : isPreviousPhoto(photo),
      ),
    )
  }

  /**
   * LocalFilePath 형식으로 이미지를 추가함
   */
  const photoType = ref<DefectPhotoType>(null)
  async function AddWithLocalFilePath(
    shapeDatabaseId: string,
    localFilePath: LocalFileResourcePath,
    localPhotoDatabaseId: string,
    isTestPhoto: boolean = false,
  ): Promise<void> {
    // 기존 -1로 설정되던 아이디를 랜덤키+_need_to_upload 으로 변경함.
    // 사유: 기존 -1로 설정되던 아이디는 중복처리되어 여러개의 요소 추가되는 문제가 있었음.
    // app인 경우, 로컬에 이미지를 저장하고, web인 경우 base64로 변환해서 저장함.
    const isPrevPhoto = localFilePath.includes('_prev')
    const currentType = isPrevPhoto ? PHOTO_TYPE.PREV : PHOTO_TYPE.NEW
    photoType.value = currentType

    // 새 사진 생성 시 초기 fields_updated_at 설정
    const currentTime = Merge.getCurrentTime()
    const initialFieldsUpdatedAt: { [key: string]: string } = {
      url: currentTime,
      is_previous: currentTime,
      localShapeId: currentTime,
    }

    // 부모 defect에서 facility_id, floorplan_id 역추적
    const shapeStore = useShapeStore()
    const parentDefect = shapeStore.GetItem(shapeDatabaseId)

    const photo = {
      id: `${localPhotoDatabaseId}_need_to_upload`,
      defect_id: -1,
      localStringId: localPhotoDatabaseId,
      localShapeId: shapeDatabaseId,
      url: localFilePath,
      is_previous: isPrevPhoto ? true : false,
      is_main: false,
      fields_updated_at: initialFieldsUpdatedAt,
      modifiedFromLocal: true,
      task_id: Number(currentTaskDatabaseId.value),
      // 로컬 생성 행도 전역 동기화의 계정 필터에 매칭되도록 실제 company_id 부여
      company_id: useAuthStore().companyId ?? 0,
      facility_id: parentDefect?.facility_id ?? 0,
      floorplan_id: parentDefect?.floorplan_id ?? 0,
      created_user_id: 0,
      sort_key: returnLastSortKey(shapeDatabaseId, currentType),
      created_at: currentTime,
      updated_at: currentTime,
      deleted_at: null,
      inspection_type: isTestPhoto ? 'TEST_MEASURE' : 'STATUS_OUTSIDE',
    } as DefectPhoto

    const changes: ChangedItem[] = findChangedItems(
      [],
      [{ ...photo, localStringId: photo.localStringId }],
    )
    logChanges('DEFECT_PHOTOS', changes)

    await requestUpdate([photo])
  }

  async function addDefectPhotoAlreadyUploaded(shapeDatabaseId: string): Promise<void> {
    const findPhotos = getCurrentShapeDefectPhotos(shapeDatabaseId)
    const currentTime = Merge.getCurrentTime()

    for (const alreadyUploadedPhoto of findPhotos) {
      const newId = getRandomString(12, false)

      // 새 사진 생성 시 초기 fields_updated_at 설정
      const initialFieldsUpdatedAt: { [key: string]: string } = {
        url: currentTime,
        is_previous: currentTime,
        localShapeId: currentTime,
      }
      // 원본 사진의 그림도 함께 복제 (스탬프가 있어야 병합·가드가 그림을 인식)
      if (alreadyUploadedPhoto.drawing) initialFieldsUpdatedAt.drawing = currentTime

      const newPhoto = {
        ...alreadyUploadedPhoto,
        id: `${newId}_need_to_upload`,
        defect_id: -1,
        localStringId: newId,
        localShapeId: shapeDatabaseId,
        url: alreadyUploadedPhoto.url,
        is_previous: alreadyUploadedPhoto.is_previous,
        deleted_at: null,
        fields_updated_at: initialFieldsUpdatedAt,
        modifiedFromLocal: true,
        task_id: parseInt(currentTaskDatabaseId.value),
      } as DefectPhoto

      const changes: ChangedItem[] = findChangedItems(
        [],
        [{ ...newPhoto, localStringId: newPhoto.localStringId }],
      )
      logChanges('DEFECT_PHOTOS', changes)

      await requestUpdate([newPhoto])
    }
  }

  async function setTargetToPrimary(
    shapeDatabaseId: string,
    photoDatabaseId: string,
    type: DefectPhotoType = null,
  ): Promise<void> {
    const filteredDefectPhotos = getCurrentPrevPhotos(shapeDatabaseId, type)
    if (filteredDefectPhotos.length === 0) return

    const typeTargetPhotos = filteredDefectPhotos.map(photo => ({ ...photo }))

    const findCurrentPhoto = typeTargetPhotos.find(photo => photo.localStringId == photoDatabaseId)
    if (!findCurrentPhoto) return

    const changedPhoto = {
      ...findCurrentPhoto,
      fields_updated_at: { ...findCurrentPhoto.fields_updated_at },
    }
    changedPhoto.sort_key = getFirstSortKey(typeTargetPhotos)
    changedPhoto.modifiedFromLocal = true

    const fieldsUpdatedAt = Merge.findChangedField(findCurrentPhoto, changedPhoto)
    changedPhoto.fields_updated_at = fieldsUpdatedAt

    await requestUpdate([changedPhoto])

    const changes: ChangedItem[] = findChangedItems([findCurrentPhoto], [changedPhoto])
    logChanges('DEFECT_PHOTOS', changes)
    return
  }

  /**
   * 사진 그리기 저장 (그림을 모두 지우면 null)
   * 스탬프 → SQLite → 동기화 큐. deferSync면 SQLite까지만 쓰고(modifiedFromLocal 유지) 큐는
   * syncPhotoDrawing이 넣는다 — 서버 전송 디바운스는 저장 API(photoDrawing/store.ts)가 한다.
   * @param photoLocalId 사진 localStringId 또는 id
   * @param drawing 그림 데이터, 그림 없음은 null
   * @param options.deferSync 동기화 큐 등록을 미룸
   */
  async function setPhotoDrawing(
    photoLocalId: string,
    drawing: PhotoDrawing | null,
    options: { deferSync?: boolean } = {},
  ): Promise<void> {
    const findPhoto = photos.value.find(
      photo => photo.localStringId == photoLocalId || String(photo.id) == photoLocalId,
    )
    if (!findPhoto) return
    const nextDrawing = drawing ?? null
    // 기존 row에는 drawing 키가 없을 수 있다 — 키가 없으면 findChangedField가 스탬프를 찍지 않으므로 null로 맞춘다
    const before = { ...findPhoto, drawing: findPhoto.drawing ?? null }
    // 서버 jsonb는 키 순서를 바꿔 돌려주므로 순서 무관 비교
    if (isSamePhotoDrawing(before.drawing, nextDrawing)) return

    const changedPhoto = { ...before, drawing: nextDrawing, modifiedFromLocal: true }
    changedPhoto.fields_updated_at = Merge.findChangedField(before, changedPhoto)

    if (options.deferSync) {
      await onlySetSqlite([changedPhoto])
    } else {
      await requestUpdate([changedPhoto])
    }

    const changes: ChangedItem[] = findChangedItems([before], [changedPhoto])
    logChanges('DEFECT_PHOTOS', changes)
  }

  /**
   * setPhotoDrawing(deferSync)로 미뤄 둔 그림 변경을 동기화 큐에 넣는다
   * 대기 중인 job이 있으면 그 job이 실행 시 SQLite 최신 row를 읽으므로 중복 등록은 큐가 거른다
   * @param photoLocalId 사진 localStringId 또는 id
   */
  async function syncPhotoDrawing(photoLocalId: string): Promise<void> {
    // 스토어는 SQLite 이벤트로 비동기 갱신되므로 저장 직후 flush에서도 맞도록 SQLite row를 읽는다
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT * FROM ${storeKey} WHERE localStringId = ? OR id = ? LIMIT 1`,
      [photoLocalId, photoLocalId],
    )
    if (!result.values?.length) return
    const row = parseRow(result.values[0]) as DefectPhoto
    // 삭제된 사진은 삭제 job이 따로 전파한다
    if (row.deleted_at || !row.modifiedFromLocal) return
    await addApiQueue([{ item: row, itemId: row.localStringId }])
  }

  async function Delete(
    photoDatabaseId: string,
    shapeDatabaseId: string,
    type: DefectPhotoType = null,
  ): Promise<void> {
    if (!photos.value) return
    const filteredDefectPhotos = getCurrentPrevPhotos(shapeDatabaseId, type)
    if (filteredDefectPhotos.length === 0) return

    const previousPhotos = filteredDefectPhotos.map(photo => ({
      ...photo,
      fields_updated_at: { ...photo.fields_updated_at },
    }))

    const findTargetPhoto = filteredDefectPhotos.find(
      item => `${item.localStringId}` == `${photoDatabaseId}`,
    )
    if (findTargetPhoto) {
      const previousPhoto = previousPhotos.find(
        p => p.localStringId === findTargetPhoto.localStringId,
      )
      if (!previousPhoto) return

      const targetToUpdate = isApp()
        ? { ...findTargetPhoto, fields_updated_at: { ...findTargetPhoto.fields_updated_at } }
        : findTargetPhoto

      targetToUpdate.deleted_at = Merge.getCurrentTime()

      // 변경된 필드 추적 - deleted_at 필드만 업데이트
      const existingFieldsUpdatedAt = targetToUpdate.fields_updated_at || {}
      const fieldsUpdatedAt = Merge.setFieldUpdate(existingFieldsUpdatedAt, 'deleted_at')
      fieldsUpdatedAt.need_restore = false
      targetToUpdate.fields_updated_at = fieldsUpdatedAt
      targetToUpdate.modifiedFromLocal = true

      await requestUpdate([targetToUpdate])
    }
    const changes: ChangedItem[] = findChangedItems(
      previousPhotos.map(photo => ({ ...photo, localStringId: photo.localStringId })),
      filteredDefectPhotos.map(photo => ({ ...photo, localStringId: photo.localStringId })),
    )
    logChanges('DEFECT_PHOTOS', changes)
    return
  }

  const getIsPreviousHasDefectPhoto = (shapeDatabaseId: string): boolean => {
    const shapeStore = useShapeStore()
    const findShape = shapeStore.GetItemRaw(shapeDatabaseId)
    const shapeInspectionType = findShape?.inspection_type ?? 'STATUS_OUTSIDE'

    const currentPhotos = getCurrentShapeDefectPhotos(shapeDatabaseId)
    const findMainPhoto = currentPhotos.find(
      item =>
        !isPreviousPhoto(item) &&
        (item.inspection_type ?? 'STATUS_OUTSIDE') === shapeInspectionType,
    )
    if (findMainPhoto) return true
    return !!findShape?.is_current
  }
  function returnLastSortKey(shapeDatabaseId: string, type: DefectPhotoType): string {
    const shapeIds = resolveShapeMatchIds(shapeDatabaseId)
    const lastSortKey = getLastSortKey(
      photos.value.filter(photo =>
        isPhotoOfShape(photo, shapeIds) && !photo.deleted_at && type === 'new'
          ? !isPreviousPhoto(photo)
          : isPreviousPhoto(photo),
      ),
    )
    return lastSortKey
  }
  // soft-delete된 사진을 복원
  async function restoreDefectPhoto(
    currentPhotoId: string,
    shapeDatabaseId: string,
  ): Promise<void> {
    const isNeedMain = getIsPreviousHasDefectCurrentPhoto(shapeDatabaseId)
    const findPhoto = photos.value.find(
      defectPhoto => `${defectPhoto.localStringId}` == `${currentPhotoId}`,
    )
    const previousPhoto = findPhoto
      ? { ...findPhoto, fields_updated_at: { ...findPhoto.fields_updated_at } }
      : undefined
    if (!findPhoto) return

    const targetToUpdate = isApp()
      ? { ...findPhoto, fields_updated_at: { ...findPhoto.fields_updated_at } }
      : findPhoto
    targetToUpdate.deleted_at = null

    if (getIsPreviousHasDefectPhoto(targetToUpdate.localShapeId.toString())) {
      targetToUpdate.sort_key = getLastSortKey(photos.value)
    }
    if (isPreviousShape(shapeDatabaseId) && !isNeedMain) {
      targetToUpdate.sort_key = getFirstSortKey(photos.value)
    }

    if (previousPhoto) {
      const fieldsUpdatedAt = Merge.findChangedField(previousPhoto, targetToUpdate)
      fieldsUpdatedAt.need_restore = true
      targetToUpdate.fields_updated_at = fieldsUpdatedAt
    }
    targetToUpdate.modifiedFromLocal = true

    if (!previousPhoto) return
    const changes: ChangedItem[] = findChangedItems([previousPhoto], [targetToUpdate])
    logChanges('DEFECT_PHOTOS', changes)
    await requestUpdate([targetToUpdate])
  }

  // 사진 변경으로 인한 전차 결함 스타일 동기화
  // ⚠️ is_current=1(강제 현차)이면 사진 유무와 관계없이 현차로 취급 — 보라색 강제 금지
  async function onColorChanged(
    shape: Defect,
    isPreviousHasDefectPhoto: boolean,
    shapeColor: string,
  ): Promise<void> {
    const shapeStore = useShapeStore()
    // is_current=1이면 '강제 현차'이므로 사진 유무 무시하고 현차 분기로 처리
    const treatAsCurrent = isPreviousHasDefectPhoto || shape.is_current === 1
    if (treatAsCurrent) {
      // 현차에 해당: 보라색이 아니면 변경 불필요
      if (shapeColor != PREV_DEFECT_STYLE.color) return
      const shapeInStore = shapeStore.GetItem(shape.localStringId)
      const saved = shapeInStore ? consumeSavedCurrentStyles(shapeInStore) : undefined
      if (saved) {
        await shapeStore.updateColorToHistory(
          shape.localStringId,
          saved.color ?? getCurrentColor('red'),
          saved.balloonFontColor ?? getCurrentColor('red'),
          saved.balloonFillColor ?? getCurrentColor('none'),
          isPreviousShape(shape.previous_defect_id),
          true,
        )
      } else {
        const canvasStore = useCanvasStore()
        const styles = canvasStore.getCurrentCreateStyles()
        await shapeStore.updateColorToHistory(
          shape.localStringId,
          styles.balloon_border_color,
          styles.balloon_font_color,
          styles.balloon_fill_color,
          isPreviousShape(shape.previous_defect_id),
          true,
        )
      }
    } else {
      // 진짜 전차(사진 없음 + is_current=0) → 보라색 강제
      const shapeInStore = shapeStore.GetItem(shape.localStringId)
      if (shapeInStore && getCurrentColorName(shapeInStore.color) !== 'purple') {
        saveCurrentStylesToShapeData(shapeInStore)
      }
      await shapeStore.updateColorToHistory(
        shape.localStringId,
        PREV_DEFECT_STYLE.color,
        PREV_DEFECT_STYLE.balloonFontColor,
        PREV_DEFECT_STYLE.balloonFillColor,
        isPreviousShape(shape.previous_defect_id),
        true,
      )
    }
  }

  // 모든 전차 결함의 색상을 일괄 재평가 (initData/backgroundSync 후 호출)
  // SQLite에서 직접 조회하여 shapes.value 전체 의존 제거
  async function reevaluateAllPrevColors(): Promise<void> {
    try {
      const db = await SQL.getDatabase()
      if (!SQL.createdTables.has('defects')) return
      const taskId = useAllTaskStore().currentTaskId
      if (!taskId) return
      const result = await db.query(
        // eslint-disable-next-line quotes
        `SELECT localStringId, previous_defect_id FROM defects WHERE task_id = ? AND (deleted_at IS NULL OR deleted_at = '') AND previous_defect_id IS NOT NULL AND previous_defect_id != ''`,
        [taskId],
      )
      const prevShapes = (result.values ?? []) as Array<{
        localStringId: string
        previous_defect_id: string
      }>
      for (const shape of prevShapes) {
        if (!isPreviousShape(shape.previous_defect_id)) continue
        await reevaluatePrevCurrentColor(shape.localStringId)
      }
    } catch {
      // shape store 미초기화 시 무시 — importEntities가 나중에 올바른 색상 적용
    }
  }

  // inspection_type 변경 등으로 전차/현차 색상 재평가가 필요할 때 호출
  async function reevaluatePrevCurrentColor(shapeDatabaseId: string): Promise<void> {
    const shapeStore = useShapeStore()
    const currentShape = shapeStore.GetItem(shapeDatabaseId)
    if (!currentShape) return
    if (!isPreviousShape(currentShape.previous_defect_id)) return
    const canvasStore = useCanvasStore()
    const currentColor = canvasStore.getShapeColorById(shapeDatabaseId)?.getColor()
    await onColorChanged(
      { ...currentShape },
      getIsPreviousHasDefectCurrentPhoto(shapeDatabaseId),
      currentColor ?? '',
    )
  }

  // 사진 삭제 + undo/redo 히스토리 등록
  async function removeDefectPhotoToHistory(
    photoDatabaseId: string,
    shapeDatabaseId: string,
    type: DefectPhotoType = null,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const shapeStore = useShapeStore()
    const findPhoto = photos.value.find(
      defectPhoto => `${defectPhoto.localStringId}` == `${photoDatabaseId}`,
    )
    if (!findPhoto) return
    await Delete(photoDatabaseId, shapeDatabaseId, type)

    const currentPhotoId = findPhoto.localStringId
    const currentIndex = canvasStore.getIndex(shapeDatabaseId.toString())
    undoRedo.execute(
      `${currentIndex ? `${currentIndex}번 ` : ''}결함 사진 삭제가`,
      async () => {
        await restoreDefectPhoto(currentPhotoId, shapeDatabaseId)
        const getShapeColor = canvasStore.getShapeColorById(shapeDatabaseId)?.getColor()
        const currentShape = shapeStore.GetItem(shapeDatabaseId)
        if (!currentShape) return
        onColorChanged(
          { ...currentShape },
          getIsPreviousHasDefectCurrentPhoto(shapeDatabaseId),
          getShapeColor ?? '',
        )
      },
      async () => {
        await Delete(currentPhotoId, shapeDatabaseId, type)
        const getShapeColor = canvasStore.getShapeColorById(shapeDatabaseId)?.getColor()
        const currentShape = shapeStore.GetItem(shapeDatabaseId)
        if (!currentShape) return
        onColorChanged(
          { ...currentShape },
          getIsPreviousHasDefectCurrentPhoto(shapeDatabaseId),
          getShapeColor ?? '',
        )
      },
    )
  }

  /** 사진 추가 + undo/redo 히스토리 등록 */
  async function AddWithLocalFilePathToHistory(
    shapeDatabaseId: string,
    localFilePath: LocalFileResourcePath,
    localPhotoDatabaseId: string,
    isTestPhoto: boolean = false,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const shapeStore = useShapeStore()

    await AddWithLocalFilePath(shapeDatabaseId, localFilePath, localPhotoDatabaseId, isTestPhoto)

    const currentIndex = canvasStore.getIndex(shapeDatabaseId.toString())
    undoRedo.execute(
      `${currentIndex ? `${currentIndex}번 ` : ''}결함 사진 추가가`,
      async () => {
        await Delete(localPhotoDatabaseId, shapeDatabaseId)
        const getShapeColor = canvasStore.getShapeColorById(shapeDatabaseId)?.getColor()
        const currentShape = shapeStore.GetItem(shapeDatabaseId)
        if (!currentShape) return
        onColorChanged(
          { ...currentShape },
          getIsPreviousHasDefectCurrentPhoto(shapeDatabaseId),
          getShapeColor ?? '',
        )
      },
      async () => {
        await restoreDefectPhoto(localPhotoDatabaseId, shapeDatabaseId)
        const getShapeColor = canvasStore.getShapeColorById(shapeDatabaseId)?.getColor()
        const currentShape = shapeStore.GetItem(shapeDatabaseId)
        if (!currentShape) return
        onColorChanged(
          { ...currentShape },
          getIsPreviousHasDefectCurrentPhoto(shapeDatabaseId),
          getShapeColor ?? '',
        )
      },
    )
  }

  const getIsPreviousHasDefectCurrentPhoto = (shapeDatabaseId: string): boolean => {
    const shapeStore = useShapeStore()
    const findShape = shapeStore.GetItem(shapeDatabaseId)
    const shapeInspectionType = findShape?.inspection_type ?? 'STATUS_OUTSIDE'

    const currentPhotos = getCurrentShapeDefectPhotos(shapeDatabaseId)
    const findMainPhotos = currentPhotos.filter(
      item =>
        !isPreviousPhoto(item) &&
        (item.inspection_type ?? 'STATUS_OUTSIDE') === shapeInspectionType,
    )
    return findMainPhotos.length > 0
  }

  const getPreviousShapesPreviousImages = (shapeDatabaseId: string): DefectPhoto[] => {
    return getCurrentShapeDefectPhotos(shapeDatabaseId).filter(item => isPreviousPhoto(item))
  }

  const getCurrentShapesCurrentImages = (shapeDatabaseId: string): DefectPhoto[] => {
    return getCurrentShapeDefectPhotos(shapeDatabaseId).filter(item => !isPreviousPhoto(item))
  }

  const getCurrentShapesTestImages = (shapeDatabaseId: string): DefectPhoto[] => {
    return getCurrentShapeDefectPhotos(shapeDatabaseId).filter(
      item => item.inspection_type === 'TEST_MEASURE',
    )
  }

  function allClear(): void {
    // 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('defect_photos', currentListener)
      currentListener = null
    }
    photos.value = []
  }

  async function getAllModifiedData(): Promise<DefectPhoto[]> {
    const db = await SQL.getDatabase()
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return localData.values?.map((row: DefectPhoto) => parseRow(row)) as DefectPhoto[]
  }

  /**
   * 업로드되지 않은 defect photo들을 soft delete 처리
   * @param {string} defectLocalStringId - defect의 localStringId
   * @param {string} deletedTime - 삭제 시간
   * @returns {Promise<void>}
   */
  async function deleteNotUploadDefectPhotos(
    defectLocalStringId: string,
    deletedTime: string,
  ): Promise<void> {
    const findPhotos = photos.value.filter(
      defectPhoto =>
        `${defectPhoto.localShapeId}` == `${defectLocalStringId}` &&
        defectPhoto.id.toString().includes('_need_to_upload') &&
        !defectPhoto.deleted_at &&
        defectPhoto.modifiedFromLocal,
    )
    if (!findPhotos || findPhotos.length === 0) return

    const findPhotoLocalStringIds = findPhotos.map(photo => photo.localStringId)

    // SQLite에서 업데이트
    const db = await SQL.getDatabase()
    const eventLogStore = useDeviceEventLogStore()
    for (const photo of findPhotos) {
      await db.query(
        'UPDATE defect_photos SET deleted_at = ?, modifiedFromLocal = ? WHERE localStringId = ?',
        [deletedTime, 0, photo.localStringId],
      )
      // 업로드 큐에서 제거
      syncQueue.removeJobByLocalStringId(photo.localStringId)

      // 이벤트 로그도 soft delete
      await eventLogStore.deleteLogs(photo.localStringId, deletedTime)
    }

    // 이벤트 발행 (리스너 완료 대기)
    if (findPhotoLocalStringIds.length > 0) {
      await SQLiteEventBus.getInstance().emitUpdate(
        'defect_photos',
        'delete',
        findPhotoLocalStringIds,
      )
    }
  }

  /**
   * soft delete된 defect photo들을 복원
   * @param {string} defectLocalStringId - defect의 localStringId
   * @returns {Promise<void>}
   */
  async function restoreNotUploadDefectPhotos(defectLocalStringId: string): Promise<void> {
    const findPhotos = photos.value
      .filter(
        defectPhoto =>
          `${defectPhoto.localShapeId}` == `${defectLocalStringId}` &&
          defectPhoto.id.toString().includes('_need_to_upload') &&
          defectPhoto.deleted_at &&
          !defectPhoto.modifiedFromLocal,
      )
      .map(photo => ({ ...photo, fields_updated_at: { ...photo.fields_updated_at } }))
    if (!findPhotos || findPhotos.length === 0) return

    const findPhotoLocalStringIds = findPhotos.map(photo => photo.localStringId)

    // SQLite에서 업데이트
    const db = await SQL.getDatabase()
    for (const localStringId of findPhotoLocalStringIds) {
      await db.query(
        'UPDATE defect_photos SET deleted_at = NULL, modifiedFromLocal = ? WHERE localStringId = ?',
        [1, localStringId],
      )
    }

    // 이벤트 발행 (리스너 완료 대기)
    if (findPhotoLocalStringIds.length > 0) {
      await SQLiteEventBus.getInstance().emitUpdate(
        'defect_photos',
        'update',
        findPhotoLocalStringIds,
      )
    }

    const findNowCurrentPhotos = photos.value.filter(photo =>
      findPhotoLocalStringIds.includes(photo.localStringId),
    )
    if (!findNowCurrentPhotos || findNowCurrentPhotos.length === 0) return

    await requestUpdate(findNowCurrentPhotos)
  }
  return {
    photos,
    notUploadedDefectPhotos,
    addDefectPhotoAlreadyUploaded,
    AddWithLocalFilePath,
    AddWithLocalFilePathToHistory,
    Delete,
    initData,
    uploadNotUploaded,
    requestUpdate,
    getIsPreviousHasDefectPhoto,
    getIsPreviousHasDefectCurrentPhoto,
    reevaluatePrevCurrentColor,
    getPreviousShapesPreviousImages,
    getCurrentShapesCurrentImages,
    allTypePhotos,
    getCurrentPrevPhotos,
    setTargetToPrimary,
    setPhotoDrawing,
    syncPhotoDrawing,
    allClear,
    getAllModifiedData,
    addApiQueue,
    requeuePhotosOfShape,
    onlySetSqlite,
    removeDefectPhotoToHistory,
    getDefectPhotosByTaskId,
    getCurrentShapesTestImages,
    deleteNotUploadDefectPhotos,
    restoreNotUploadDefectPhotos,
  }
})
